import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { getSavedMenuFilterGroup } from "../../../../store/actions";
import Flex from "../../../../UI/Flex/Flex";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const MinusContainer = styled.span(
  ({ theme, isOverride }) => `
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${isOverride ? theme.primary : theme.notification.warningMain};
  border-radius: 5px;
  margin-right: 10px;
  cursor: ${isOverride ? "auto" : "pointer"};
`
);

const ValueContainer = styled.div`
  margin-left: 45px;
  font-style: italic;
  font-size: 12px;
`;

export default function useEditRememberedSettings(props) {
  const { uuid, filters, overridenFilters, updateGroup } = props;

  const [group, setGroup] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    async function fetch() {
      try {
        const res = await dispatch(getSavedMenuFilterGroup(uuid)).unwrap();
        setGroup(res.data.data);
      } catch (err) {
        throw new Error(err);
      }
    }
    fetch();
  }, [dispatch, uuid]);

  const buildItems = useCallback(
    (settings, isOverride = false) => {
      return settings?.reduce((acc, curr) => {
        const filter = isOverride
          ? curr
          : filters.find((filter) => filter.uuid === curr.menuFilter?.uuid);

        if (filter) {
          acc.push({
            displayName: filter.displayName,
            values: isOverride
              ? curr.values.filter((v) => v.checked).map((v) => v.value)
              : curr.values,
            uuid: filter.uuid,
            name: filter.name,
          });
        }

        return acc;
      }, []);
    },
    [filters]
  );

  const checkboxItems = useMemo(() => {
    if (!group) {
      return [];
    }

    return buildItems(group.menuFilterSettings);
  }, [buildItems, group]);

  const overridenItems = useMemo(() => {
    return buildItems(overridenFilters, true);
  }, [buildItems, overridenFilters]);

  const removeFilterItem = (uuid) => {
    setGroup((group) => {
      return {
        ...group,
        menuFilterSettings: group.menuFilterSettings.filter(
          (item) => item.menuFilter.uuid !== uuid
        ),
      };
    });
  };

  const onUpdate = (isOverride = false) => {
    const items = isOverride ? overridenItems : group.menuFilterSettings;

    const payload = {
      uuid: group.uuid,
      name: group.name,
      isDefault: group.isDefault,
      menuFilters: items.map((item) => ({
        menuFilterUuid: isOverride ? item?.uuid : item?.menuFilter?.uuid,
        values: item?.values,
      })),
      isOverride,
    };
    updateGroup(payload);
  };

  const renderItems = (items, isOverride = false) => {
    return items.map((filter, index) => (
      <div key={filter.uuid + index} style={{ marginBottom: 10 }}>
        <Flex>
          <MinusContainer isOverride={isOverride}>
            <FontAwesomeIcon
              icon={["fas", isOverride ? "plus" : "minus"]}
              onClick={() =>
                isOverride ? null : removeFilterItem(filter.uuid)
              }
            />
          </MinusContainer>
          <b>{filter.displayName}</b>&nbsp;[{filter.name}]:
        </Flex>
        {filter.values.map((val, index) => {
          return <ValueContainer key={`${val}-${index}`}>{val}</ValueContainer>;
        })}
      </div>
    ));
  };

  return {
    group,
    setGroup,
    checkboxItems,
    overridenItems,
    onUpdate,
    renderItems,
  };
}
